import React from "react";
import { Link } from "react-router-dom";
import { useT } from "../lib/i18n";
import useSEO from "../lib/useSEO";
import { breadcrumbLd, businessLd } from "../lib/jsonld";

export default function About() {
  const { t } = useT();
  useSEO({
    title: "About WarehousesUAE — Operators, Not Brokers | Dubai Industrial Real Estate",
    description: "WarehousesUAE.com — powered by Blueworld Realestate. A Dubai-based commercial brokerage focused exclusively on industrial property. We work with operators, supply-chain managers and logistics directors — not tourists.",
    canonical: window.location.origin + "/about",
    image: window.location.origin + "/og-default.png",
    jsonLd: [businessLd(), breadcrumbLd([{ name: "Home", url: "/" }, { name: "About", url: "/about" }])],
  });
  return (
    <div data-testid="about-page" className="bg-zinc-50 min-h-screen">
      <div className="bg-zinc-950 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bp-grid-dark opacity-30"></div>
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="font-mono-tech text-[10px] uppercase text-[#FFCC00] mb-3">{t("about.hero_kicker")}</div>
            <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95]">
              {t("about.hero_title_a")}<br />{t("about.hero_title_b")}<br /><span className="text-[#FFCC00]">{t("about.hero_title_c")}</span>
            </h1>
          </div>
          <div className="flex items-end">
            <p className="text-zinc-300 leading-relaxed text-lg max-w-md">
              {t("about.hero_sub")}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <img src="https://images.unsplash.com/photo-1601599963565-b7f49deb3d50?w=1200" alt="Warehouse" className="w-full h-full object-cover border border-zinc-200" />
        <div>
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-3">{t("about.approach_kicker")}</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter mb-5">{t("about.approach_title")}</h2>
          <div className="space-y-4 text-zinc-700 leading-relaxed">
            <p>{t("about.approach_p1")}</p>
            <p>{t("about.approach_p2")}</p>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-px bg-zinc-200">
            {[
              { v: "12+", l: t("about.stat_years") },
              { v: "240+", l: t("about.stat_props") },
              { v: "98%", l: t("about.stat_ret") },
            ].map((s, i) => (
              <div key={i} className="bg-white p-5">
                <div className="font-display text-3xl tracking-tighter text-[#031833]">{s.v}</div>
                <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border-y border-zinc-200 py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-3">{t("about.principles_kicker")}</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter mb-10">{t("about.principles_title")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200">
            {[
              { title: t("about.p1_t"), d: t("about.p1_d") },
              { title: t("about.p2_t"), d: t("about.p2_d") },
              { title: t("about.p3_t"), d: t("about.p3_d") },
              { title: t("about.p4_t"), d: t("about.p4_d") },
            ].map((p, i) => (
              <div key={i} className="bg-white p-6">
                <div className="font-mono-tech text-[10px] text-[#031833] mb-2">0{i + 1} //</div>
                <h3 className="font-display text-xl tracking-tighter mb-2">{p.title}</h3>
                <p className="text-sm text-zinc-600">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 text-center">
        <h2 className="font-display text-4xl md:text-5xl tracking-tighter mb-4">{t("about.cta_title")}</h2>
        <div className="flex gap-3 justify-center flex-wrap">
          <Link to="/contact" className="bg-[#031833] text-white font-mono-tech text-sm uppercase px-8 py-4 hover:bg-[#0A2455]">{t("about.cta_btn1")}</Link>
          <Link to="/properties" className="border border-zinc-900 font-mono-tech text-sm uppercase px-8 py-4 hover:bg-zinc-900 hover:text-white">{t("about.cta_btn2")}</Link>
        </div>
      </div>
    </div>
  );
}
